<template>
	<!-- ponytail: Alt/Option + right-click copies the current on-screen text for internal LLM pasting; hidden, unannounced, native right-click untouched -->
	<div ref="copyContent" @contextmenu="onContextMenu">
		<div
			ref="exportContent"
			class="p-6 text-white rounded-lg bg-neutral-900"
		>
			<div
				v-if="headerFirst && !hideHeader"
				data-test="overview-header"
				:class="
					wheelMode === 'comparison' || inlineHeader
						? 'flex flex-col gap-6 mb-8 sm:flex-row sm:items-start sm:justify-between'
						: ''
				"
			>
				<slot name="header-prefix" />

				<OverviewSegmentedControl
					v-if="wheelMode === 'comparison'"
					v-model="selectedMode"
					:disabled="!isWorkStyleVisible"
				/>

				<OverviewHeader
					:heading="overviewHeading"
					:inline="wheelMode === 'comparison' || inlineHeader"
					:wheel-mode="wheelMode"
					:class="{
						'sm:flex-1': wheelMode === 'comparison' || inlineHeader
					}"
				>
					<template #actions>
						<slot name="header-actions" />
					</template>
				</OverviewHeader>
			</div>

			<slot name="before-content" />

			<div
				v-if="!headerFirst && !hideHeader"
				data-test="overview-header"
				:class="
					wheelMode === 'comparison' || inlineHeader
						? 'flex flex-col gap-6 mb-6 sm:flex-row sm:items-start sm:justify-between'
						: ''
				"
			>
				<slot name="header-prefix" />

				<OverviewSegmentedControl
					v-if="wheelMode === 'comparison'"
					v-model="selectedMode"
					:disabled="!isWorkStyleVisible"
				/>

				<OverviewHeader
					:heading="overviewHeading"
					:inline="wheelMode === 'comparison' || inlineHeader"
					:wheel-mode="wheelMode"
					:class="{
						'sm:flex-1': wheelMode === 'comparison' || inlineHeader
					}"
				>
					<template #actions>
						<slot name="header-actions" />
					</template>
				</OverviewHeader>
			</div>

			<slot name="after-header" />

			<OverviewComparisonContent
				v-if="!hideProfileContent && activeMode === 'comparison'"
				:job-offer-data="jobOfferData"
				:applicant-data="applicantData"
				:match-data="matchData"
				:verdict="fetchedVerdict"
				:side="verdictSide"
				wheel-mode="comparison"
				@insight-opened="$emit('insight-opened', $event)"
			/>

			<OverviewProfileContent
				v-else-if="!hideProfileContent"
				:job-offer-data="jobOfferData"
				:applicant-data="applicantData"
				:wheel-mode="activeMode"
			/>
		</div>

		<InsightsMethodologyCard
			v-if="!hideProfileContent && activeMode === 'comparison'"
			:side="verdictSide"
			class="mt-6"
			data-test="insights-methodology-card"
		/>

		<OverviewExportMenu
			v-if="exportMenu.open"
			:x="exportMenu.x"
			:y="exportMenu.y"
			@export="exportAsImage"
			@close="closeExportMenu"
		/>

		<!-- Off-screen export tree: mounted only while capturing, so the live
			panel (accordion state, scroll position) is never mutated. -->
		<div
			v-if="exportTreeVisible"
			data-test="export-tree-host"
			class="fixed top-0 -left-[10000px] pointer-events-none"
			aria-hidden="true"
		>
			<OverviewExportTree
				ref="exportTree"
				:job-offer-data="jobOfferData"
				:applicant-data="applicantData"
				:match-data="matchData"
				:verdict="fetchedVerdict"
				:side="verdictSide"
				:active-mode="activeMode"
				:wheel-mode="wheelMode"
				:heading="overviewHeading"
				:subject-label="exportSubjectLabel"
				:date="exportDate"
				:width="exportWidth"
			/>
		</div>
	</div>
</template>

<script>
import * as Sentry from '@sentry/browser'
import personalityResults from '~/mixins/personalityResults'
import { copyTextToClipboard } from '~/utils/clipboard'
import {
	OverviewHeader,
	OverviewExportMenu,
	OverviewExportTree,
	OverviewSegmentedControl,
	OverviewComparisonContent,
	OverviewProfileContent,
	InsightsMethodologyCard
} from './_internals'

const VERDICT_RETRY_DELAY = 2000
// 60s of polling outlasts one full 30s backend lock lease, after which this request can generate itself.
const VERDICT_RETRY_LIMIT = 30

export default {
	name: 'PersonalityResultsOverview',
	components: {
		OverviewHeader,
		OverviewExportMenu,
		OverviewExportTree,
		OverviewSegmentedControl,
		OverviewComparisonContent,
		OverviewProfileContent,
		InsightsMethodologyCard
	},
	mixins: [personalityResults],
	props: {
		jobOfferData: {
			type: Object,
			default: () => {}
		},
		applicantData: {
			type: Object,
			default: () => {}
		},
		matchData: {
			type: Object,
			required: false,
			default: null
		},
		heading: {
			type: String,
			default: null
		},
		hideFooter: {
			type: Boolean,
			required: false,
			default: false
		},
		applicationId: {
			type: [Number, String],
			required: false,
			default: null
		},
		// Resolved by the parent, which knows the viewing context and applies
		// the same masking rules as the page (talent pool shows first name
		// only). The component never builds a person name itself.
		exportSubjectName: {
			type: String,
			required: false,
			default: ''
		},
		inlineHeader: {
			type: Boolean,
			default: false
		},
		headerFirst: {
			type: Boolean,
			default: false
		},
		hideHeader: {
			type: Boolean,
			default: false
		},
		hideProfileContent: {
			type: Boolean,
			default: false
		},
		isWorkStyleVisible: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			fetchedVerdict: null,
			verdictRequestId: 0,
			verdictRetryTimer: null,
			exportDate: '',
			exportTreeVisible: false,
			exportWidth: 0,
			exportMenu: { open: false, x: 0, y: 0 }
		}
	},
	watch: {
		'$store.state.locale': 'fetchAiVerdict',
		applicationId: {
			immediate: true,
			handler: 'fetchAiVerdict'
		},
		selectedMode(next) {
			if (this.wheelMode !== 'comparison' || !this.isWorkStyleVisible) {
				return
			}

			this.$emit('mode-change', next)
		}
	},
	beforeDestroy() {
		// Invalidate an in-flight response as well as a scheduled retry.
		++this.verdictRequestId
		clearTimeout(this.verdictRetryTimer)
	},
	computed: {
		verdictSide() {
			return this.userIsApplicant ? 'applicant' : 'company'
		},
		overviewHeading() {
			// The segmented control already names the active view in comparison mode.
			if (this.wheelMode === 'comparison') {
				return ''
			}

			if (this.heading !== null) {
				return this.heading
			}

			return this.userIsApplicant
				? this.$t('personality_results.overview.applicant.heading')
				: this.$t('personality_results.overview.hiring_manager.heading')
		},
		exportSubjectLabel() {
			const given = (this.exportSubjectName || '').trim()
			if (given) {
				return given
			}
			// Safe fallbacks only: job title or profile title, never a person
			// name resolved here without the parent's masking context.
			return this.jobOfferData?.name || this.applicantData?.title || ''
		}
	},
	methods: {
		formatExportDate(date = new Date()) {
			// Local calendar date (yyyy-MM-dd). toISOString() is UTC and yields
			// yesterday between midnight and the UTC offset (e.g. 00:00–02:00
			// in Zurich during summer time).
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			return `${date.getFullYear()}-${month}-${day}`
		},
		buildExportFilename(date) {
			const slug = this.exportSubjectLabel
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-+|-+$/g, '')
			return `personality-results-${slug || this.activeMode}-${date}.png`
		},
		onContextMenu(event) {
			// Alt/Option + right-click stays the hidden text-copy gesture.
			if (event.altKey) {
				this.copyResults(event)
				return
			}

			event.preventDefault()
			this.exportMenu = { open: true, x: event.clientX, y: event.clientY }
		},
		closeExportMenu() {
			this.exportMenu.open = false
		},
		async exportAsImage() {
			if (this.exportTreeVisible) {
				return
			}

			this.exportMenu.open = false
			const exportDate = this.formatExportDate()
			this.exportDate = exportDate
			// Mirror the live panel width so the off-screen tree wraps
			// identically to what the user sees.
			this.exportWidth = this.$refs.exportContent?.offsetWidth || 0
			this.exportTreeVisible = true

			try {
				const { toPng } = await import('html-to-image')
				// Let the off-screen export tree render before capture.
				await this.$nextTick()

				const dataUrl = await toPng(this.$refs.exportTree.$el, {
					backgroundColor: '#171717', // neutral-900; PNG needs a solid bg
					pixelRatio: 2
				})

				const link = document.createElement('a')
				link.download = this.buildExportFilename(exportDate)
				link.href = dataUrl
				link.click()
			} catch (error) {
				Sentry.captureException(error)
				this.$store.dispatch('toast/push', {
					title: this.$t(
						'toast.personality-results-export-error.title'
					),
					message: this.$t(
						'toast.personality-results-export-error.message'
					),
					type: 'error'
				})
			} finally {
				this.exportTreeVisible = false
			}
		},
		async fetchAiVerdict() {
			const requestId = ++this.verdictRequestId
			clearTimeout(this.verdictRetryTimer)
			this.verdictRetryTimer = null
			this.fetchedVerdict = null
			const id = this.applicationId

			if (!id) {
				return
			}

			const isCurrentRequest = () =>
				this.applicationId === id && requestId === this.verdictRequestId
			const requestVerdict = async retries => {
				try {
					// First view may generate synchronously — allow a gateway-length wait.
					const response = await this.$axios.$get(
						`/application/${id}/ai-verdict`,
						{ timeout: 35000 }
					)

					if (!isCurrentRequest()) return

					if (response?.available) {
						this.fetchedVerdict =
							response.verdict?.[this.verdictSide] || null
					} else if (
						response?.pending === true &&
						retries < VERDICT_RETRY_LIMIT
					) {
						// A warmup owns the generation lock; retry only while this visit is current.
						this.verdictRetryTimer = setTimeout(() => {
							this.verdictRetryTimer = null
							if (isCurrentRequest()) requestVerdict(retries + 1)
						}, VERDICT_RETRY_DELAY)
					}
				} catch (error) {
					// Fail silent for the user — the comparison tab must look complete
					// without the AI section — but keep the ops signal.
					Sentry.captureException(error)
				}
			}

			return requestVerdict(0)
		},
		// ponytail: SVG wheel labels may not appear in innerText; trait/insight
		// text lives in surrounding HTML and is captured, which is what matters.
		async copyResults(event) {
			// Native right-click stays intact; Alt/Option + right-click is the hidden copy gesture.
			if (!event.altKey) return
			event.preventDefault()

			const text = this.$refs.copyContent.innerText
			if (await copyTextToClipboard(text)) {
				this.showCopiedFeedback()
			}
		},
		showCopiedFeedback() {
			this.$store.dispatch('toast/push', {
				title: this.$t('toast.personality-results-copied.title'),
				message: this.$t('toast.personality-results-copied.message'),
				type: 'success'
			})
		}
	}
}
</script>
